// LinkPreview — an honest glimpse of a URL. Nothing more.
//
// Two surfaces live here:
//
//   `LinkPreview`       — pure presentational card. Takes a resolved
//                         preview payload and renders it. Storybook
//                         drives every state through this.
//
//   `LinkPreviewCard`   — the container the timeline actually mounts.
//                         Gates on viewport visibility, wires up
//                         `useLinkPreview`, shows a brief skeleton,
//                         and renders nothing when the fetch fails.
//
// Both consume only `--chat-*` tokens. Every pixel, duration, and
// colour reads off the theme so light + dark + reduced-motion all
// behave the same way for free. The hover state is owned by the
// stylesheet via the `chat-link-preview` class; React never re-renders
// on pointer movement.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useLinkPreview } from "~/hooks/useLinkPreview";
import { rememberPreviewHeight, rememberedPreviewHeight } from "~/lib/link-preview-layout";
import type { Client, LinkPreview as LinkPreviewData } from "~/lib/matrix";
import { openInSystemBrowser } from "~/lib/markdown-render";

/**
 * 2× the displayed thumbnail size (`--chat-link-preview-thumbnail-size`,
 * 80px). Synapse's `/thumbnail` endpoint serves the scaled variant the
 * browser caches independently of the original. Parsed from the token
 * at call time so a theme override in tests picks up automatically.
 */
const THUMBNAIL_REQUEST_PX_FALLBACK = 160;

/**
 * Read a numeric CSS custom property off `:root` — accepts values
 * tagged `px` (pixel tokens) or `ms` (duration tokens), plus plain
 * numbers. Returns `fallback` when the property is missing or the
 * parse fails. Used only for imperative JavaScript surfaces —
 * `<img width>` / `<img height>` attributes and the
 * `setTimeout(loading timeout)` — never for layout, which the
 * stylesheet owns. Graceful under jsdom where
 * `getComputedStyle` returns an empty string.
 */
function readNumericToken(name: string, fallback: number): number {
  if (typeof window === "undefined" || typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const parsed = raw.endsWith("ms")
    ? Number.parseFloat(raw.slice(0, -2))
    : raw.endsWith("px")
      ? Number.parseFloat(raw.slice(0, -2))
      : Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Pure presentational card. Renders a resolved preview; no fetching,
 * no loading state, no IntersectionObserver wiring. Stories use this
 * directly; the timeline uses `LinkPreviewCard` which composes it.
 *
 * `resolveThumbnail` is injected so stories can pass a fixture URL
 * without a live Matrix client — the default resolver (see
 * `LinkPreviewCard`) goes through `mxcToHttp`.
 */
export interface LinkPreviewProps {
  preview: LinkPreviewData;
  /** Absolute URL the card links to. */
  url: string;
  /**
   * Resolve `preview.imageMxc` to an `<img src>`. Injected so stories
   * can render fixture data without a Matrix client. When omitted or
   * `preview.imageMxc` is absent, the card renders text-only.
   */
  resolveThumbnail?: (imageMxc: string) => string;
}

export function LinkPreview({ preview, url, resolveThumbnail }: LinkPreviewProps) {
  const rawHost = hostnameFromUrl(url);
  const siteName = preview.siteName ?? (rawHost.startsWith("www.") ? rawHost.slice(4) : rawHost);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const thumbnailSrc =
    preview.imageMxc && resolveThumbnail && !thumbnailFailed
      ? resolveOrNull(resolveThumbnail, preview.imageMxc)
      : undefined;
  // Thumbnail width/height attributes use the same size the card
  // renders; a fallback keeps jsdom happy.
  const thumbnailSize = readNumericToken("--chat-link-preview-thumbnail-size", 80);
  // Accessible label is derived from what the user actually reads. A
  // title collapses to the headline; a title-less preview falls back
  // to the site name — never to the raw URL, which a screen reader
  // would vocalise character by character ("h-t-t-p-s-colon-slash…").
  const accessibleName = preview.title
    ? `Open ${preview.title}`
    : preview.description
      ? `Open ${preview.description}`
      : `Open link on ${siteName}`;
  // Thumbnails get an alt string when one is available — the ticket
  // explicitly asks for alt text, and a title almost always describes
  // the image well enough. Truly decorative (no title / description /
  // site name) thumbnails fall back to empty alt so screen readers
  // don't announce "image" on something that carries no signal.
  const thumbnailAlt = preview.title ?? preview.description ?? "";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={openInSystemBrowser(url)}
      aria-label={accessibleName}
      className="chat-button chat-link-preview"
      style={{
        display: "flex",
        alignItems: "stretch",
        minHeight: 0,
        maxWidth: "var(--chat-link-preview-max-width)",
        borderRadius: "var(--chat-radius-md)",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "var(--chat-space-3)",
          flex: 1,
          minWidth: 0,
          padding: "var(--chat-space-3) var(--chat-space-4)",
        }}
      >
        <div
          style={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              color: "var(--chat-fg-readable)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-xs-size)",
              lineHeight: "var(--chat-type-body-xs-line)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {siteName}
          </span>
          {preview.title && (
            <span
              style={{
                marginTop: "var(--chat-link-preview-baseline-gap)",
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
                fontWeight: 300,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {preview.title}
            </span>
          )}
          {preview.description && (
            <span
              style={{
                marginTop: "var(--chat-space-1)",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {preview.description}
            </span>
          )}
        </div>
        {preview.imageMxc && !thumbnailFailed && !thumbnailSrc && (
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: "var(--chat-link-preview-thumbnail-size)",
              height: "var(--chat-link-preview-thumbnail-size)",
              borderRadius: "var(--chat-radius-sm)",
              background: "var(--chat-surface-panel)",
              alignSelf: "center",
            }}
          />
        )}
        {thumbnailSrc && (
          <img
            src={thumbnailSrc}
            alt={thumbnailAlt}
            onError={() => setThumbnailFailed(true)}
            width={thumbnailSize}
            height={thumbnailSize}
            loading="lazy"
            decoding="async"
            style={{
              flexShrink: 0,
              width: "var(--chat-link-preview-thumbnail-size)",
              height: "var(--chat-link-preview-thumbnail-size)",
              borderRadius: "var(--chat-radius-sm)",
              objectFit: "cover",
              alignSelf: "center",
            }}
          />
        )}
      </div>
    </a>
  );
}

/**
 * Quiet placeholder shown while the preview fetch is in flight. It
 * has the finished card's fixed height, so the timeline doesn't jump
 * when the payload arrives.
 */
function LinkPreviewSkeleton({ height }: { height?: number }) {
  return (
    <div
      aria-hidden
      className="chat-link-preview-skeleton"
      style={{
        maxWidth: "var(--chat-link-preview-max-width)",
        height: height ?? "var(--chat-link-preview-skeleton-height)",
        borderRadius: "var(--chat-radius-md)",
        background: "var(--chat-surface-panel)",
      }}
    />
  );
}

/**
 * Timeline-facing container. Mounts a visibility observer on its own
 * wrapper; only starts the preview fetch once the card is inside (or
 * within the rootMargin of) the scroll viewport. A card that scrolls
 * off-screen before the fetch completes continues — the hook's cache
 * keeps the payload ready for the next time the row reappears.
 *
 * Slow previews hold the placeholder until the request settles, so a
 * row never collapses and then re-expands for a late card.
 */
export interface LinkPreviewCardProps {
  client: Client | null;
  url: string;
  /**
   * Event timestamp (`origin_server_ts`) the Synapse preview cache
   * uses as a key. When omitted the server falls back to "now", which
   * is correct for interactive uses but misses the per-event cache
   * that makes two clients render the same preview from one origin
   * fetch. The timeline is expected to thread the event's `ts`
   * through.
   */
  ts?: number;
  /**
   * Optional override for the IntersectionObserver root. Defaults to
   * the document viewport. The timeline scroll container is the natural
   * root when earlier gating is desired; the virtualizer already
   * unmounts off-screen rows, so viewport gating is sufficient in
   * practice.
   */
  visibilityRoot?: Element | null;
}

export function LinkPreviewCard({ client, url, ts, visibilityRoot }: LinkPreviewCardProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  // A link seen before starts at once, holding the space its card took last time.
  const remembered = useMemo(() => rememberedPreviewHeight(url), [url]);
  const active = visible || remembered !== undefined;
  const { preview, isLoading } = useLinkPreview(client, active ? url : null, ts);

  // Visibility observer — fire once when the card enters the viewport
  // and then stop. The hook cache makes subsequent mounts cheap, so
  // there is no benefit to flipping `visible` back to false when the
  // card scrolls away; keeping it stuck at `true` avoids a flicker if
  // the virtualizer re-measures the row.
  useEffect(() => {
    if (visible) return;
    const el = wrapperRef.current;
    if (!el) return;
    // Tauri webview + every modern browser ship IntersectionObserver.
    // Guard anyway so unit tests in jsdom don't explode.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            return;
          }
        }
      },
      {
        root: visibilityRoot ?? null,
        // 200px of slack so the card is already fetching by the time
        // it scrolls into view — stays off the critical timeline path
        // (nothing triggers until the user is clearly heading towards
        // the row) but avoids a visible "skeleton → content" flash.
        rootMargin: "200px 0px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, visibilityRoot]);

  // The thumbnail itself is fetched through the authenticated v1
  // media path because Synapse ≥ 1.120's
  // `enable_authenticated_media: true` rejects the unauthenticated
  // v3 URL — see `useAuthenticatedMedia` for the rationale. The hook
  // sits one level up so `LinkPreview` (the pure presentational
  // component) stays driven by props alone; this container injects
  // a resolver that returns the consumer-owned blob URL.
  const requestPx = readNumericToken(
    "--chat-link-preview-thumbnail-request-px",
    THUMBNAIL_REQUEST_PX_FALLBACK,
  );
  const thumbnailMxc = active && preview?.imageMxc ? preview.imageMxc : null;
  const thumbnailMedia = useAuthenticatedMedia(client, thumbnailMxc, {
    thumbnail: { w: requestPx, h: requestPx, method: "scale" },
  });
  const resolveThumbnail = useMemo(() => {
    const blobUrl = thumbnailMedia.src;
    if (!blobUrl) return undefined;
    // The pure `LinkPreview` calls the resolver with the same
    // `imageMxc` we already used to drive the hook; we ignore the
    // argument and return the resolved blob URL the hook produced.
    return () => blobUrl;
  }, [thumbnailMedia.src]);

  // Reserved space holds until the request settles; a late card never re-expands a collapsed row.
  const pending = !active || isLoading;
  let body: ReactNode = null;
  if (pending) {
    if (remembered !== undefined) {
      body = remembered > 0 ? <LinkPreviewSkeleton height={remembered} /> : null;
    } else {
      // Visibility gates the fetch, never the frame: the row has its
      // final footprint from first paint.
      body = <LinkPreviewSkeleton />;
    }
  } else if (preview && hasRenderableContent(preview)) {
    body = (
      <LinkPreview
        preview={thumbnailMedia.error ? { ...preview, imageMxc: undefined } : preview}
        url={url}
        resolveThumbnail={resolveThumbnail}
      />
    );
  }

  useLayoutEffect(() => {
    // A timed-out fetch says nothing about the link, so only real results are remembered.
    // Without a client nothing was fetched, so there is nothing to remember.
    if (!client || pending || isLoading) return;
    const card = wrapperRef.current?.firstElementChild;
    rememberPreviewHeight(url, card ? card.getBoundingClientRect().height : 0);
  }, [client, pending, isLoading, url, preview, resolveThumbnail]);

  // Spacing is owned by `.chat-link-preview-wrapper` in chat.css:
  //   - The first wrapper with a visible card sits 8px below the
  //     message body.
  //   - Subsequent siblings that also render a card collapse to 4px.
  //   - Wrappers whose card failed (empty `body`) contribute no
  //     margin, so a failed first card does not leave a spacer and a
  //     failed middle card does not promote the next sibling to a
  //     "first" gap.
  // The inline style only owns the left inset (16px when visible),
  // which has no sibling dependency.
  return (
    <div
      ref={wrapperRef}
      className="chat-link-preview-wrapper"
      style={{
        display: "block",
        marginLeft: 0,
      }}
    >
      {body}
    </div>
  );
}

/**
 * A preview is renderable when it carries at least one of the three
 * signal-bearing fields (title / description / `imageMxc`). Site name
 * alone is not enough — "Open link on matrix.org" with no title or
 * image is weaker than the inline link the parser already renders,
 * and the brief's card makes no sense without at least one of title,
 * description, or image.
 */
function hasRenderableContent(preview: LinkPreviewData): boolean {
  return Boolean(preview.title || preview.description || preview.imageMxc);
}

function hostnameFromUrl(raw: string): string {
  try {
    return new URL(raw).hostname;
  } catch {
    return raw;
  }
}

function resolveOrNull(
  resolver: (imageMxc: string) => string,
  imageMxc: string,
): string | undefined {
  try {
    const out = resolver(imageMxc);
    return out || undefined;
  } catch {
    return undefined;
  }
}
